import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAppHref, shouldUseExternalAuthLinks, signupHref } from '@/utils/authLinks';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (shouldUseExternalAuthLinks) {
      window.location.replace(authAppHref);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    // Simulate auth flow
    setTimeout(() => setLoading(false), 2000);
  };

  if (shouldUseExternalAuthLinks) {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream-50 flex">
      {/* Left — Image */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20serene%20morning%20scene%20with%20soft%20golden%20light%20filtering%20through%20linen%20curtains%20a%20warm%20minimalist%20bedroom%20with%20natural%20wood%20furniture%20a%20small%20ceramic%20cup%20of%20coffee%20on%20a%20wooden%20side%20table%20peace%20and%20calm%20mood%20muted%20earth%20tones%20beige%20and%20cream%20editorial%20photography%20style%20no%20text%20no%20people&width=900&height=1200&seq=auth1&orientation=portrait"
          alt="Calm morning atmosphere"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal-400/30 via-transparent to-charcoal-400/10" />
        <div className="absolute bottom-12 left-10 right-10 z-10">
          <h2 className="font-serif text-3xl text-white font-medium leading-snug mb-3">
            Welcome back to your
            <br />
            <span className="italic font-light">peaceful practice.</span>
          </h2>
          <p className="text-white/70 text-sm leading-relaxed max-w-xs">
            Reconnect with what matters. Continue your journey of intentional stewardship.
          </p>
        </div>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 lg:px-20">
        <div className="w-full max-w-sm">
          {/* Mobile header */}
          <div className="lg:hidden mb-10 text-center">
            <Link
              to="/"
              className="text-xs font-semibold tracking-[0.2em] uppercase text-charcoal-400 hover:text-sage-500 transition-colors"
            >
              LET MONEY SERVE
            </Link>
          </div>

          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h1 className="font-serif text-2xl md:text-3xl text-charcoal-400 font-medium mb-2">
              Sign In
            </h1>
            <p className="text-sm text-charcoal-100 mb-8 leading-relaxed">
              Continue where you left off on your stewardship journey.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div
              className={`transition-all duration-700 delay-100 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <label htmlFor="email" className="block text-xs font-medium text-charcoal-300 mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 bg-white border border-sand-100 rounded-lg text-sm text-charcoal-400 placeholder:text-charcoal-50 focus:outline-none focus:border-sage-300 focus:ring-1 focus:ring-sage-300 transition-all"
              />
            </div>

            <div
              className={`transition-all duration-700 delay-200 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <label htmlFor="password" className="block text-xs font-medium text-charcoal-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full px-4 py-3 bg-white border border-sand-100 rounded-lg text-sm text-charcoal-400 placeholder:text-charcoal-50 focus:outline-none focus:border-sage-300 focus:ring-1 focus:ring-sage-300 transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-charcoal-100 hover:text-charcoal-300 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <div
              className={`flex items-center justify-between transition-all duration-700 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-sand-100 text-sage-500 focus:ring-sage-300 accent-sage-500"
                />
                <span className="text-xs text-charcoal-200">Remember me</span>
              </label>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="text-xs text-sage-500 hover:text-sage-600 transition-colors"
              >
                Forgot password?
              </a>
            </div>

            <div
              className={`transition-all duration-700 delay-400 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-charcoal-400 text-white py-3 rounded-full text-sm font-medium hover:bg-charcoal-300 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-8 mb-8 flex items-center gap-4">
            <span className="flex-1 h-px bg-sand-100" />
            <span className="text-xs text-charcoal-50 font-medium">or</span>
            <span className="flex-1 h-px bg-sand-100" />
          </div>

          {/* Social / Alternative */}
          <div
            className={`space-y-3 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <button
              type="button"
              className="w-full flex items-center justify-center gap-3 bg-white border border-sand-100 text-charcoal-300 py-3 rounded-full text-sm font-medium hover:bg-sand-50 transition-all duration-300"
            >
              <i className="ri-google-fill text-base" />
              Continue with Google
            </button>
          </div>

          {/* Footer link */}
          <p
            className={`mt-8 text-center text-sm text-charcoal-200 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Do not have an account?{' '}
            <a
              href={signupHref}
              className="text-sage-500 font-medium hover:text-sage-600 transition-colors"
            >
              Create one
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
